import { useUserStore } from "@/stores/useUserStore";
import Image from "next/image"
import { useEffect, useState } from "react";
import { MenuItemProps } from "../Utils/constants";
import DatePicker from 'react-datepicker';
import axios from "axios";
import { CountriesItemProps } from "../Utils/constants";
import { getNewToken } from "@/lib/Definitions";
import AlertDialog from "../Utils/AlertDialog";

import "react-datepicker/dist/react-datepicker.css";


interface InputStates {
  name: boolean;
  email: boolean;
  password: boolean;
  birthday: boolean;
  phone: boolean;
  gender: boolean;
}

const Account = (props: MenuItemProps) => {
  const { userInfo, getUser, updateUser } = useUserStore();
  const [userData, setUserData] = useState(getUser());
  const [name, setName] = useState(userData.name);
  const [email, setEmail] = useState(userData.email);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [birthday, setBirthday] = useState<Date | null>(userData.birthday && userData.birthday.length > 0 ? new Date(userData.birthday) : null);
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState(userData.phone);
  const [gender, setGender] = useState(userData.gender);
  const [countries, setCountries] = useState<Array<CountriesItemProps>>([]);
  const [init, setInit] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [inputStates, setInputStates] = useState({
    name: true,
    email: true,
    password: true,
    birthday: true,
    phone: true,
    gender: true,
  });

  const today = new Date();

  const handleToggleReadOnly = (inputName: keyof InputStates) => {
    if (!inputStates[inputName]) {
      handleUpdate(inputName);
    }

    setInputStates((prevState) => ({
      ...prevState,
      [inputName]: !prevState[inputName]
    }));
  }

  const handleUpdate = async (field: keyof InputStates) => {
    let value: string | null = "";

    switch (field) {
      case "name":
        value = name;
        break;

      case "email":
        value = email;
        break;

      case "password":
        value = password;
        break;

      case "birthday":
        value = birthday !== null ? `${birthday.getFullYear()}-${(birthday.getMonth() + 1).toString().padStart(2, '0')}-${birthday.getDate()}` : null;
        break;

      case "phone":
        if (country !== "" && phone !== "") {
          value = countries.find(c => c.country === country)?.code + phone;
        }
        break;

      case "gender":
        value = gender;
        break;
    }

    if (value !== "" && value !== null) {
      if (field === "password") {
        if (password !== newPassword) {
          setPassword("");
          setNewPassword("");
          setMessage("Password doesn't match");
          return;
        } else {
          try {
            const response = await axios.put(
              "/api/password",
              { password: password },
              { headers: { Authorization: 'Bearer ' + userData.token } }
            );
    
            if (response.data.status === "ok") {
              setPassword("");
              setNewPassword("");
              setMessage("Password has been changed, please use the new password to login.");
            }
          } catch (error: any) {
            if (error.response.data.error === "Invalid credentials") {
              const response: any = await getNewToken(userData.token, userData.refresh_token, userData.uuid);
  
              if (response !== "") {
                userData.token = response.token;
                userData.refresh_token = response.refresh_token;
                updateUser(userData);
                handleUpdate(field);
              }
            } else {
              setMessage("Wrong update password, please try again");
            }
          }
        }
      } else {
        try {
          const response = await axios.put(
            "/api/user",
            { [field]: value },
            { headers: { Authorization: 'Bearer ' + userData.token } }
          )
    
          if (response.status === 200) {
            if (field === "phone") {
              userData.country_code = countries.find(c => c.country === country)?.code!;
              userData.phone = phone;
            } else {
              userData[field] = value;
            }

            updateUser(userData);
          }
        } catch (error: any) {
          if (error.response.data.error === "Invalid credentials") {
            const response: any = await getNewToken(userData.token, userData.refresh_token, userData.uuid);

            if (response !== "") {
              userData.token = response.token;
              userData.refresh_token = response.refresh_token;
              updateUser(userData);
              handleUpdate(field);
            }
          }
        }
      }
    }
  }

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(
          '/api/countries',
          { headers: { Authorization: 'Bearer ' + userData.token } }
        )

        if (response.data.status === "ok") {
          setCountries(response.data.data);

          if (userData.country_code !== "") {
            const cc = response.data.data.find((c: any) => c.code === userData.country_code);

            if (cc !== undefined) {
              setCountry(cc.country);
            }
          }
        }
      } catch (error: any) {
        if (error.response.data.error === "Invalid credentials") {
          const response: any = await getNewToken(userData.token, userData.refresh_token, userData.uuid);

          if (response !== "") {
            userData.token = response.token;
            userData.refresh_token = response.refresh_token;
            updateUser(userData);
            fetchCountries();
          }
        }
      } finally {
        setIsLoading(false);
      }
    }

    if (init) {
      setIsLoading(true);
      fetchCountries();
      setInit(false);
    }
  }, [init, updateUser, userData])

  useEffect(() => {
    if (message !== "") {
      setOpen(true);
    }
  }, [message]);

  useEffect(() => {
    if (!open) {
      setMessage("");
    }
  }, [open]);

  useEffect(() => {
    setUserData(getUser());
  }, [getUser, userInfo])

  return (
    <div className="w-full">
      <div className="flex items-center space-x-4">
        <div>
          <Image
            src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/user-account-icon.png"}
            alt="Account Detail icon"
            width={20}
            height={20}
            quality={100}
            className="hidden md:block"
          />
          <Image
            src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/arrow-right.svg"}
            alt="left arrow icon"
            width={24}
            height={24}
            quality={100}
            className="rotate-180 md:hidden"
            onClick={() => props.setSelected("")}
          />
        </div>
        <p className="text-base text-[#2C3E2F] font-dmsans font-bold">Account Details</p>
      </div>
      <div className="flex justify-between mt-6">
        <input
          type="text"
          value={name}
          className={`h-9 p-2 text-sm ${inputStates.name ? "text-[#C6C6C6]" : "text-[#2C3E2F]"} w-full border-b border-[#C6C6C6] border-solid bg-[#FCFCF8] focus:outline-none`}
          placeholder="Name"
          onChange={event => setName(event.target.value)}
          disabled={inputStates.name}
        />
        <button
          className="underline py-1 px-2 text-xs text-[#C6C6C6] border-b border-[#C6C6C6] border-solid bg-[#FCFCF8]"
          onClick={() => handleToggleReadOnly("name")}
        >
          {inputStates.name ? "Edit" : "Save"}
        </button>
      </div>
      <div className="flex justify-between mt-6">
        <input
          type="text"
          value={email}
          className={`h-9 p-2 text-sm ${inputStates.email ? "text-[#C6C6C6]" : "text-[#2C3E2F]"} w-full border-b border-[#C6C6C6] border-solid bg-[#FCFCF8] focus:outline-none`}
          placeholder="Email"
          onChange={event => setEmail(event.target.value)}
          disabled={inputStates.email}
        />
        <button
          className="underline py-1 px-2 text-xs text-[#C6C6C6] border-b border-[#C6C6C6] border-solid bg-[#FCFCF8]"
          onClick={() => handleToggleReadOnly("email")}
        >
          {inputStates.email ? "Edit" : "Save"}
        </button>
      </div>
      <div className="flex justify-between mt-6 border-b border-[#C6C6C6] border-solid">
        {inputStates.password ? (
          <input
            type="password"
            className={`h-9 p-2 text-sm text-[#C6C6C6] w-full bg-[#FCFCF8] focus:outline-none`}
            placeholder="Password"
            disabled={true}
          />
        ) : (
          <>
            <div className="flex justify-between w-full pr-8">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                className={`h-9 p-2 text-sm "text-[#2C3E2F]" w-full bg-[#FCFCF8] focus:outline-none`}
                placeholder="Password"
                onChange={event => setPassword(event.target.value)}
              />
              <Image
                src={showPassword ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/eye-slash.svg" : process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/eye.svg"}
                alt="eye icon"
                width={20}
                height={20}
                quality={100}
                className="cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
              />
            </div>
            <div className="flex justify-between w-full pr-8">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                className={`h-9 p-2 text-sm "text-[#2C3E2F]" w-full bg-[#FCFCF8] focus:outline-none`}
                placeholder="New Password"
                onChange={event => setNewPassword(event.target.value)}
              />
              <Image
                src={showNewPassword ? process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/eye-slash.svg" : process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/eye.svg"}
                alt="eye icon"
                width={20}
                height={20}
                quality={100}
                className="cursor-pointer"
                onClick={() => setShowNewPassword(!showNewPassword)}
              />
            </div>
          </>
        )}
        <button
          className="underline py-1 px-2 text-xs text-[#C6C6C6] bg-[#FCFCF8]"
          onClick={() => handleToggleReadOnly("password")}
        >
          {inputStates.password ? "Edit" : "Save"}
        </button>
      </div>
      <div className="flex justify-between mt-6 border-b border-[#C6C6C6] border-solid">
        <DatePicker
          selected={birthday}
          onChange={(date) => {
            date !== null ? setBirthday(date) : ""
          }}
          className={`h-9 p-2 text-sm ${inputStates.birthday ? "text-[#C6C6C6]" : "text-[#2C3E2F]"} w-full bg-[#FCFCF8] focus:outline-none`}
          placeholderText="Birth Date"
          dateFormat="yyyy-MM-dd"
          maxDate={today}
          disabled={inputStates.birthday}
        />
        <button
          className="underline py-1 px-2 text-xs text-[#C6C6C6] bg-[#FCFCF8]"
          onClick={() => handleToggleReadOnly("birthday")}
        >
          {inputStates.birthday ? "Edit" : "Save"}
        </button>
      </div>
      <div className="flex justify-between mt-6">
        <div className="relative select-container w-full">
          <select
            name="country"
            value={country}
            className={`h-9 p-2 text-sm ${inputStates.phone ? "text-[#C6C6C6]" : "text-[#2C3E2F]"} w-full border-b border-[#C6C6C6] border-solid bg-[#FCFCF8] focus:outline-none`}
            onChange={event => setCountry(event.target.value)}
            disabled={inputStates.phone}
          >
            <option value="">Select Country</option>
            {countries.map((country, index) => (
              <option value={country.country} key={index}>{country.country + " (" + country.code + ")"}</option>
            ))}
          </select>
          <Image
            src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/dropdown.png"}
            alt="dropdown icon"
            width={24}
            height={24}
            quality={100}
            className="absolute right-0 -translate-y-1/2 pointer-events-none top-1/2"
          />
        </div>
        <input
          type="text"
          value={phone}
          className={`h-9 p-2 text-sm ${inputStates.phone ? "text-[#C6C6C6]" : "text-[#2C3E2F]"} w-full border-b border-[#C6C6C6] border-solid bg-[#FCFCF8] focus:outline-none`}
          placeholder="Phone"
          onChange={event => setPhone(event.target.value)}
          disabled={inputStates.phone}
        />
        <button
          className="underline py-1 px-2 text-xs text-[#C6C6C6] border-b border-[#C6C6C6] border-solid bg-[#FCFCF8]"
          onClick={() => handleToggleReadOnly("phone")}
        >
          {inputStates.phone ? "Edit" : "Save"}
        </button>
      </div>
      <div className="flex justify-between mt-6">
        <div className="relative select-container w-full">
          <select
            name="gender"
            value={gender}
            className={`h-9 p-2 text-sm ${inputStates.gender ? "text-[#C6C6C6]" : "text-[#2C3E2F]"} w-full border-b border-[#C6C6C6] border-solid bg-[#FCFCF8] focus:outline-none`}
            onChange={event => setGender(event.target.value)}
            disabled={inputStates.gender}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
          <Image
            src={process.env.NEXT_PUBLIC_STATIC_SITE + "/images/icon/dropdown.png"}
            alt="dropdown icon"
            width={24}
            height={24}
            quality={100}
            className="absolute right-0 -translate-y-1/2 pointer-events-none top-1/2"
          />
        </div>
        <button
          className="underline py-1 px-2 text-xs text-[#C6C6C6] border-b border-[#C6C6C6] border-solid bg-[#FCFCF8]"
          onClick={() => handleToggleReadOnly("gender")}
        >
          {inputStates.gender ? "Edit" : "Save"}
        </button>
      </div>
      <AlertDialog message={message} open={open} setOpen={setOpen} />
    </div>
  )
}

export default Account
